import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import config from '../config';

const initialForm = {
  name: '',
  phone: '',
  email: '',
  business: '',
  consent: false,
};

const SmsOptInPage = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (field) => (e) => {
    const value = field === 'consent' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.name.trim() || !form.phone.trim()) {
      setError('Name and phone number are required.');
      return;
    }
    if (!form.consent) {
      setError('You must check the consent box to opt in.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${config.API_BASE_URL}/optin/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim() || null,
          business: form.business.trim() || null,
          consent: true,
          consent_text:
            'I agree to receive SMS messages from Car Wash Copilot (Winborne Software LLC) ' +
            'regarding my account and the information I have requested. Message frequency varies ' +
            '(typically fewer than 5 messages/month). Message & data rates may apply. Reply STOP ' +
            'to opt out or HELP for help.',
          consent_url: typeof window !== 'undefined' ? window.location.href : null,
          consent_timestamp: new Date().toISOString(),
        }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Request failed (${res.status})`);
      }

      setSubmitted(true);
      setForm(initialForm);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-lg shadow p-6 sm:p-8 text-center">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">You're opted in.</h1>
          <p className="text-sm text-gray-600">
            We've sent a confirmation SMS to the number you provided. You can reply{' '}
            <strong>STOP</strong> at any time to opt out, or <strong>HELP</strong> for help.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setSubmitted(false)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Submit another request
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">SMS Opt-In</h1>
        <p className="mt-2 text-sm text-gray-600">
          Opt in to receive SMS messages from Car Wash Copilot. After submitting, we'll send a
          confirmation text to the number you provide.
        </p>
      </div>

      {/* Disclosures — visible above the fold */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-xs text-blue-900 space-y-1">
        <p><strong>Sender:</strong> Car Wash Copilot (Winborne Software LLC), from +1 (888) 255-0484.</p>
        <p><strong>Message types:</strong> account, setup, and operational notifications (e.g., work order assignments, billing alerts). No marketing.</p>
        <p><strong>Frequency:</strong> varies &mdash; typically fewer than 5 messages per month.</p>
        <p><strong>Message &amp; data rates may apply.</strong></p>
        <p><strong>Opt out:</strong> reply <strong>STOP</strong>. <strong>Help:</strong> reply <strong>HELP</strong>, email help@carwashcopilot.com, or call (484) 525-0026.</p>
        <p>
          See the{' '}
          <Link to="/messaging-consent" className="underline hover:text-blue-700">
            Messaging Terms
          </Link>{' '}
          for full details.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-4 sm:p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={handleChange('name')}
            required
            autoComplete="name"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="John Smith"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Mobile Phone Number <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={form.phone}
            onChange={handleChange('phone')}
            required
            autoComplete="tel"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="(555) 123-4567"
          />
          <p className="mt-1 text-xs text-gray-500">
            U.S. mobile numbers only. We'll send a confirmation text here.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
          <input
            type="email"
            value={form.email}
            onChange={handleChange('email')}
            autoComplete="email"
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="you@example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Business Name (optional)</label>
          <input
            type="text"
            value={form.business}
            onChange={handleChange('business')}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Ocean City Car Wash"
          />
        </div>

        {/* Required consent checkbox */}
        <label className="flex items-start gap-3 pt-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.consent}
            onChange={handleChange('consent')}
            className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
          />
          <span className="text-xs text-gray-700 leading-relaxed">
            I agree to receive SMS messages from Car Wash Copilot (Winborne Software LLC)
            regarding my account and the information I have requested. Message frequency varies
            (typically fewer than 5 messages/month). Message &amp; data rates may apply. Reply{' '}
            <strong>STOP</strong> to opt out or <strong>HELP</strong> for help. See our{' '}
            <Link to="/messaging-consent" className="text-blue-600 hover:text-blue-800 underline">
              Messaging Terms
            </Link>
            .
          </span>
        </label>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2.5 rounded-md font-medium"
        >
          {submitting ? 'Submitting...' : 'Opt In to SMS'}
        </button>

        <p className="text-xs text-gray-500 text-center">
          By submitting this form you confirm the phone number belongs to you and that you are
          authorized to consent to SMS at that number.
        </p>
      </form>
    </div>
  );
};

export default SmsOptInPage;
